import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { DollarSign, Save, Settings, Percent, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';

function AdminPaymentSettings({ userInfo }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    'lab-advertisement': { name: '기공소 홍보', price: 30000, duration: 30, description: '기공소 디렉토리에 홍보 등록' },
    'seminar': { name: '세미나 등록', price: 50000, duration: 60, description: '세미나 게시 및 홍보' },
    'job-posting': { name: '구인공고 등록', price: 20000, duration: 30, description: '구인구직 게시판에 공고 등록' },
    'advertisement': { name: '광고 등록', basic: { price: 50000, duration: 30, description: '베이직 광고' }, standard: { price: 100000, duration: 30, description: '스탠다드 광고' }, premium: { price: 200000, duration: 30, description: '프리미엄 광고' } },
    'new-product': { name: '신제품 등록', price: 30000, duration: 60, description: '신제품 소개 및 홍보' },
    'marketplace': { name: '마켓플레이스 수수료', commissionRate: 5, description: '상품 판매 시 매출의 일정 비율' }
  });

  useEffect(() => {
    if (!isAdminUser(userInfo)) {
      alert('관리자만 접근 가능합니다.');
      window.location.href = '/';
      return;
    }
    loadSettings();
  }, [userInfo]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'paymentPrices'));
      if (settingsDoc.exists()) setSettings(prev => ({ ...prev, ...settingsDoc.data().prices }));
    } catch (error) {
      console.error('설정 로드 실패:', error);
      showMessage('error', '설정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!window.confirm('결제 금액을 변경하시겠습니까?\n\n변경된 금액은 즉시 적용됩니다.')) return;
    try {
      setSaving(true);
      await setDoc(doc(db, 'systemSettings', 'paymentPrices'), {
        prices: settings,
        updatedBy: userInfo.uid,
        updatedByName: userInfo.name || userInfo.email,
        updatedAt: serverTimestamp()
      }, { merge: true });
      showMessage('success', '결제 설정이 저장되었습니다!');
    } catch (error) {
      console.error('설정 저장 실패:', error);
      showMessage('error', '설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handlePriceChange = (serviceType, value, tier = null) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      if (serviceType === 'advertisement' && tier) newSettings[serviceType][tier].price = parseInt(value) || 0;
      else if (serviceType === 'marketplace') newSettings[serviceType].commissionRate = parseFloat(value) || 0;
      else newSettings[serviceType].price = parseInt(value) || 0;
      return newSettings;
    });
  };

  const handleDurationChange = (serviceType, value, tier = null) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      if (serviceType === 'advertisement' && tier) newSettings[serviceType][tier].duration = parseInt(value) || 0;
      else if (serviceType !== 'marketplace') newSettings[serviceType].duration = parseInt(value) || 0;
      return newSettings;
    });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleReset = () => {
    if (!window.confirm('모든 설정을 기본값으로 초기화하시겠습니까?')) return;
    setSettings({
      'lab-advertisement': { name: '기공소 홍보', price: 30000, duration: 30, description: '기공소 디렉토리에 홍보 등록' },
      'seminar': { name: '세미나 등록', price: 50000, duration: 60, description: '세미나 게시 및 홍보' },
      'job-posting': { name: '구인공고 등록', price: 20000, duration: 30, description: '구인구직 게시판에 공고 등록' },
      'advertisement': { name: '광고 등록', basic: { price: 50000, duration: 30 }, standard: { price: 100000, duration: 30 }, premium: { price: 200000, duration: 30 } },
      'new-product': { name: '신제품 등록', price: 30000, duration: 60, description: '신제품 소개 및 홍보' },
      'marketplace': { name: '마켓플레이스 수수료', commissionRate: 5, description: '상품 판매 시 매출의 일정 비율' }
    });
    showMessage('success', '기본값으로 초기화되었습니다.');
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>설정을 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIconBox}>
            <Settings size={20} color="#ffffff" />
          </div>
          <span style={styles.headerTitle}>결제 설정</span>
        </div>
      </div>

      {/* 액션 버튼 */}
      <div style={styles.actionBar}>
        <button onClick={handleReset} style={styles.resetBtn} disabled={saving}>
          <RefreshCw size={16} />
          <span>초기화</span>
        </button>
        <button onClick={handleSave} style={styles.saveBtn} disabled={saving}>
          {saving ? <div style={styles.smallSpinner}></div> : <Save size={16} />}
          <span>{saving ? '저장 중...' : '저장'}</span>
        </button>
      </div>

      {/* 메시지 */}
      {message.text && (
        <div style={{ ...styles.message, ...(message.type === 'success' ? styles.messageSuccess : styles.messageError) }}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* 설정 카드 */}
      <div style={styles.cardsContainer}>
        {/* 일반 서비스 */}
        {['lab-advertisement', 'seminar', 'job-posting', 'new-product'].map((key) => (
          <ServiceCard
            key={key}
            service={settings[key]}
            serviceType={key}
            onPriceChange={handlePriceChange}
            onDurationChange={handleDurationChange}
          />
        ))}

        {/* 광고 (티어별) */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.cardIconBox, backgroundColor: '#f3e8ff' }}>
              <DollarSign size={20} color="#8b5cf6" />
            </div>
            <div>
              <h3 style={styles.cardTitle}>{settings['advertisement'].name}</h3>
              <p style={styles.cardDesc}>티어별 광고 금액 설정</p>
            </div>
          </div>
          <div style={styles.cardBody}>
            {[
              { key: 'basic', label: '베이직', color: '#64748b' },
              { key: 'standard', label: '스탠다드', color: '#3b82f6' },
              { key: 'premium', label: '프리미엄', color: '#f59e0b' }
            ].map(tier => (
              <div key={tier.key} style={styles.tierSection}>
                <span style={{ ...styles.tierBadge, backgroundColor: tier.color }}>{tier.label}</span>
                <div style={styles.inputRow}>
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>금액</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        value={settings['advertisement'][tier.key].price}
                        onChange={(e) => handlePriceChange('advertisement', e.target.value, tier.key)}
                        style={styles.input}
                      />
                      <span style={styles.inputSuffix}>원</span>
                    </div>
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>기간</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        value={settings['advertisement'][tier.key].duration}
                        onChange={(e) => handleDurationChange('advertisement', e.target.value, tier.key)}
                        style={styles.input}
                      />
                      <span style={styles.inputSuffix}>일</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 마켓플레이스 수수료 */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.cardIconBox, backgroundColor: '#ccfbf1' }}>
              <Percent size={20} color="#14b8a6" />
            </div>
            <div>
              <h3 style={styles.cardTitle}>{settings['marketplace'].name}</h3>
              <p style={styles.cardDesc}>{settings['marketplace'].description}</p>
            </div>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>판매 수수료율</label>
              <div style={styles.inputWrapper}>
                <input
                  type="number"
                  value={settings['marketplace'].commissionRate}
                  onChange={(e) => handlePriceChange('marketplace', e.target.value)}
                  style={styles.input}
                  step="0.5"
                />
                <span style={styles.inputSuffix}>%</span>
              </div>
            </div>
            <div style={styles.exampleBox}>
              <AlertCircle size={14} color="#0369a1" />
              <span>예시: 100,000원 판매 시 {(100000 * settings['marketplace'].commissionRate / 100).toLocaleString()}원 수수료</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ServiceCard({ service, serviceType, onPriceChange, onDurationChange }) {
  const iconColors = {
    'lab-advertisement': '#6366f1',
    'seminar': '#10b981',
    'job-posting': '#f59e0b',
    'new-product': '#ec4899'
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={{ ...styles.cardIconBox, backgroundColor: `${iconColors[serviceType]}20` }}>
          <DollarSign size={20} color={iconColors[serviceType]} />
        </div>
        <div>
          <h3 style={styles.cardTitle}>{service.name}</h3>
          <p style={styles.cardDesc}>{service.description}</p>
        </div>
      </div>
      <div style={styles.cardBody}>
        <div style={styles.inputRow}>
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>등록 금액</label>
            <div style={styles.inputWrapper}>
              <input
                type="number"
                value={service.price}
                onChange={(e) => onPriceChange(serviceType, e.target.value)}
                style={styles.input}
              />
              <span style={styles.inputSuffix}>원</span>
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>게시 기간</label>
            <div style={styles.inputWrapper}>
              <input
                type="number"
                value={service.duration}
                onChange={(e) => onDurationChange(serviceType, e.target.value)}
                style={styles.input}
              />
              <span style={styles.inputSuffix}>일</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9' },

  // 헤더
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', backgroundColor: '#6366f1' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  headerIconBox: { width: '36px', height: '36px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#ffffff' },

  // 액션 바
  actionBar: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px' },
  resetBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#ffffff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  saveBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#6366f1', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },
  smallSpinner: { width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #ffffff', borderRadius: '50%', animation: 'spin 1s linear infinite' },

  // 메시지
  message: { display: 'flex', alignItems: 'center', gap: '10px', margin: '0 14px 14px', padding: '14px', borderRadius: '10px', fontSize: '14px', fontWeight: '600' },
  messageSuccess: { backgroundColor: '#d1fae5', color: '#065f46' },
  messageError: { backgroundColor: '#fee2e2', color: '#991b1b' },

  // 카드
  cardsContainer: { display: 'flex', flexDirection: 'column', gap: '10px', padding: '0 14px 14px' },
  card: { backgroundColor: '#ffffff', borderRadius: '14px', padding: '14px' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' },
  cardIconBox: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardTitle: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' },
  cardDesc: { margin: '2px 0 0', fontSize: '12px', color: '#64748b' },
  cardBody: { display: 'flex', flexDirection: 'column', gap: '12px' },

  // 입력
  inputRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  inputLabel: { fontSize: '12px', fontWeight: '600', color: '#64748b' },
  inputWrapper: { position: 'relative' },
  input: { width: '100%', padding: '12px', paddingRight: '40px', fontSize: '15px', fontWeight: '600', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', boxSizing: 'border-box' },
  inputSuffix: { position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', fontWeight: '600', color: '#94a3b8' },

  // 티어
  tierSection: { padding: '12px', backgroundColor: '#f8fafc', borderRadius: '10px' },
  tierBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', color: '#ffffff', marginBottom: '10px' },

  // 예시
  exampleBox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', backgroundColor: '#e0f2fe', borderRadius: '8px', fontSize: '12px', color: '#0369a1' },

  // 로딩
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { fontSize: '14px', color: '#64748b' },
};

export default AdminPaymentSettings;